import React, { useState, useRef } from 'react';
import { InputLabel, OutlinedInput, Tooltip, Typography, Select, MenuItem, Button } from '@mui/material';
import axios from 'axios';
import "../../styles/UserDashboard/Kyc.scss";

// Utility function to upload a file as data URI to backend
const allowedExtensions = [
    "png", "jpg", "jpeg", "pdf", "docx", "doc", "xlsx", "csv", "openxmlformats", "octet-stream"
];

function getMediaType(file) {
    if (!file.name) return "bin";

    const ext = file.name.split('.').pop().toLowerCase();
    if (allowedExtensions.includes(ext)) return ext;
    return "bin";
}

// Add file size validation
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB


// Preview component for file gallery
const FilePreviewGallery = ({ files, onRemove, type = 'image' }) => {
    if (!Array.isArray(files) || files.length === 0) return null;

    return (
        <div style={{
            marginTop: '16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '12px'
        }}>
            {files.map((file, idx) => {
                const hasPreview = file.preview && file.preview !== null && file.preview.length > 0;
                const fileName = file.name || 'unknown file';
                const isPDF = file.type === 'application/pdf' || (fileName && fileName.toLowerCase().endsWith('.pdf'));
                const isImage = file.type?.startsWith('image/') && hasPreview;
                const fileExtension = fileName.split('.').pop() || 'file';

                return (
                    <div
                        key={`${fileName}-${idx}-${Date.now()}`}
                        style={{
                            position: 'relative',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#f0f0f0',
                            aspectRatio: '1',
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        className="file-preview-item"
                    >
                        {(isImage || (isPDF && hasPreview)) ? (
                            <img
                                src={file.preview}
                                alt={fileName}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'flex';
                                    }
                                }}
                            />
                        ) : null}

                        {/* Fallback for non-images or failed previews */}
                        <div style={{
                            display: (isImage || (isPDF && hasPreview)) ? 'none' : 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            backgroundColor: isPDF ? '#ffebee' : '#f5f5f5',
                            padding: '8px'
                        }}>
                            {isPDF ? (
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <polyline points="14,2 14,8 20,8" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <text x="12" y="18" textAnchor="middle" fontSize="8" fill="#f44336" fontWeight="bold">PDF</text>
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M13 2v7h7" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            <span style={{
                                fontSize: '9px',
                                marginTop: '4px',
                                textAlign: 'center',
                                color: '#999',
                                wordBreak: 'break-all',
                                maxWidth: '100%',
                                padding: '0 4px'
                            }}>
                                {fileExtension.toUpperCase()}
                            </span>
                        </div>

                        {/* Hover overlay with delete button */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s'
                            }}
                            className="preview-overlay"
                        >
                            <button
                                onClick={() => onRemove(idx)}
                                style={{
                                    background: '#D32F2F',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'white'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="5" y1="5" x2="13" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                    <line x1="13" y1="5" x2="5" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        <style>{`
              .file-preview-item:hover .preview-overlay {
                display: flex !important;
                opacity: 1 !important;
              }
            `}</style>

                        {/* Tooltip with filename */}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '4px',
                            fontSize: '9px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {fileName}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
const KYCGovernanceNonGreenForm = ({ onComplete, userId, token }) => {
    const [currentStep, setCurrentStep] = useState('facility');
    const [facilityInfo, setFacilityInfo] = useState({
        facilityName: '',
        facilityDescription: '',
        facilityLocation: '',
        projectStartDate: '',
        projectEndDate: '',
        fuelSource: '',
        fuelSourceOther: '',
        additonalInfo: '',

    });
    const [baselineInfo, setBaselineInfo] = useState({
        baseline: '',
        fuelSourceBaseline: '',
        proofOfFuelSource: [],
        justificationStatement: [],
    });
    const [doubleCounting, setDoubleCounting] = useState(false);
    const [showDropdownFuelSource, setShowDropdownFuelSource] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const fileStorageRef = useRef(new Map());


    async function uploadFileToBackend(fileObj, userId, token) {
        try {
            // Get the actual File object from storage using the ID
            const file = fileStorageRef.current.get(fileObj.id);

            if (!file) {
                throw new Error(`File not found in storage. ID: ${fileObj.id}`);
            }

            if (!(file instanceof File || file instanceof Blob)) {
                throw new Error(`Invalid file type: ${file?.constructor?.name}. Expected File or Blob`);
            }

            // Check file size
            if (file.size > MAX_FILE_SIZE) {
                throw new Error(`File size too large. Maximum allowed: 5MB`);
            }

            const ext = file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(ext)) {
                throw new Error("File type not allowed. Allowed: " + allowedExtensions.join(", "));
            }

            console.log('Starting to read file:', file.name);

            // Convert file to Base64
            const dataUri = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    console.log('File read successfully');
                    resolve(reader.result);
                };
                reader.onerror = (error) => {
                    console.error('FileReader error:', error);
                    reject(new Error("Failed to read file: " + error.message));
                };
            });

            const media_type = getMediaType(file);

            const apiUrl = process.env.REACT_APP_API_URL;
            if (!apiUrl) {
                throw new Error("API URL not configured. Check REACT_APP_API_URL environment variable");
            }

            if (!token) {
                throw new Error("Authentication token is missing");
            }

            if (!userId) {
                throw new Error("User ID is missing");
            }

            console.log('Uploading file:', {
                fileName: file.name,
                fileSize: file.size,
                mediaType: media_type
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/open/v1/upload`,
                {
                    media_type,
                    folder: "documents",
                    user_id: userId,
                    contents: [dataUri],
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 30000
                }
            );

            console.log('Upload response:', response.data);

            if (response?.data?.body?.success) {
                return response.data.body.data[0];
            } else if (response?.data?.success) {
                return response.data.data[0];
            } else {
                throw new Error("File upload failed: " + (response?.data?.body?.message || response?.data?.message || "Unknown error"));
            }
        } catch (error) {
            console.error('Upload error details:', error);

            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);
                throw new Error(`Upload failed (${error.response.status}): ${error.response.data?.message || error.message}`);
            } else if (error.request) {
                throw new Error("No response from server. Check your network connection and API endpoint.");
            } else {
                throw error;
            }
        }
    }

    // Clear upload error when user interacts with either upload type
    const clearUploadError = () => {
        setUploadError('');
    };

    // Helper function to create file preview - FIXED VERSION
    const createFilePreview = (file) => {
        return new Promise((resolve) => {
            const isImage = file.type?.startsWith('image/');
            const isPDF = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));

            // Ensure we have a valid file object
            if (!file || typeof file !== 'object') {
                console.error('Invalid file in createFilePreview:', file);
                resolve({
                    file: file,
                    preview: null,
                    name: 'Invalid File',
                    type: 'application/octet-stream',
                    isImage: false,
                    isPDF: false
                });
                return;
            }

            // For non-image files (PDFs, etc.), resolve immediately without preview
            if (!isImage) {
                resolve({
                    file: file, // Keep the original file intact
                    preview: null,
                    name: file.name || 'Unknown File',
                    type: file.type || 'application/octet-stream',
                    isImage: false,
                    isPDF: isPDF
                });
                return;
            }

            // Only create preview for images
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve({
                    file: file, // Keep the original file intact
                    preview: e.target.result,
                    name: file.name || 'Unknown File',
                    type: file.type || 'application/octet-stream',
                    isImage: true,
                    isPDF: false
                });
            };
            reader.onerror = () => {
                resolve({
                    file: file, // Keep the original file intact
                    preview: null,
                    name: file.name || 'Unknown File',
                    type: file.type || 'application/octet-stream',
                    isImage: false,
                    isPDF: isPDF
                });
            };
            reader.readAsDataURL(file);
        });
    };


    // Generic file upload handler
    const handleFileUpload = async (e, fieldName) => {
        setUploadError('');
        const files = Array.from(e.target.files);

        // Check file sizes before processing
        const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
        if (oversizedFiles.length > 0) {
            setUploadError(`File size too large. Maximum allowed: 5MB`);
            return;
        }

        // Create file display objects with unique IDs
        const filesWithPreviews = [];

        for (const file of files) {
            const isImage = file.type?.startsWith('image/');
            const isPDF = file.type === 'application/pdf' ||
                (file.name && file.name.toLowerCase().endsWith('.pdf'));

            let preview = null;

            // Only create preview for images
            if (isImage) {
                try {
                    preview = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve(e.target.result);
                        reader.onerror = () => resolve(null);
                        reader.readAsDataURL(file);
                    });
                } catch (err) {
                    console.error('Error creating preview:', err);
                    preview = null;
                }
            }

            // Create unique ID for this file
            const fileId = `file-${Date.now()}-${Math.random()}`;

            // Store the actual File object in the ref Map
            fileStorageRef.current.set(fileId, file);

            filesWithPreviews.push({
                id: fileId,
                name: file.name,
                type: file.type,
                size: file.size,
                preview: preview,
                isImage: isImage,
                isPDF: isPDF
            });
        }

        setBaselineInfo(prev => ({
            ...prev,
            [fieldName]: [...(prev[fieldName] || []), ...filesWithPreviews]
        }));
    };

    // Generic file removal handler
    const handleRemoveFile = (fieldName, index) => {
        setUploadError('');
        setBaselineInfo(prev => {
            const fileToRemove = prev[fieldName][index];
            // Clean up the stored file from ref
            if (fileToRemove?.id) {
                fileStorageRef.current.delete(fileToRemove.id);
            }
            return {
                ...prev,
                [fieldName]: prev[fieldName].filter((_, i) => i !== index)
            };
        });
    };

    const handleProofOfFuelSourceUpload = (e) => handleFileUpload(e, 'proofOfFuelSource');
    const handleRemoveProofOfFuelSource = (index) => handleRemoveFile('proofOfFuelSource', index);

    const handleJustificationStatementUpload = (e) => handleFileUpload(e, 'justificationStatement');
    const handleRemoveJustificationStatement = (index) => handleRemoveFile('justificationStatement', index);

    const handleFacilityChange = (field, value) => {
        setFacilityInfo(prev => ({ ...prev, [field]: value }));
    };
    const handleBaselineChange = (field, value) => {
        setBaselineInfo(prev => ({ ...prev, [field]: value }));
    };
    const validateFacilityForm = () => {
        const requiredFields = ['facilityName', 'facilityDescription', 'facilityLocation', 'projectStartDate', 'projectEndDate', 'fuelSource'];
        return requiredFields.every(field => {
            if (Array.isArray(facilityInfo[field])) {
                return facilityInfo[field].length > 0;
            }
            return facilityInfo[field] && typeof facilityInfo[field] === 'string' && facilityInfo[field].trim() !== '';
        });
    };
    const validateBaselineForm = () => {
        const requiredFields = [
            { key: 'baseline', type: 'string' },
            { key: 'fuelSourceBaseline', type: 'string' },
        ];

    };

    const handleFacilityComplete = (e) => {
        e.preventDefault();

        // Validate form fields first
        if (!validateFacilityForm()) {
            alert('Please fill in all required fields before continuing.');
            return;
        }

        // Check double counting validation
        if (doubleCounting === "true") {
            alert(
                "Projects receiving or seeking credit for reductions and removals from a project activity under another GHG program are not accepted on CloseCarbon"
            );
            return;
        }

        // Validate project dates
        if (Date.now() > Date.parse(facilityInfo.projectEndDate)) {
            alert("Project end date can not be in the past");
            return;
        }

        if (Date.parse(facilityInfo.projectStartDate) > Date.parse(facilityInfo.projectEndDate)) {
            alert("Project start date can not be ahead of Project end date ");
            return;
        }

        setCurrentStep('baseline');
    };
    const handleBackToFacility = (e) => {
        e.preventDefault();
        setCurrentStep('facility');
    };
    const handleDone = async (e) => {
        e.preventDefault();
        if (!validateBaselineForm()) {
            const errors = [];


            const hasProofOfFuel = baselineInfo.proofOfFuelSource && baselineInfo.proofOfFuelSource.length > 0;
            const hasJustification = baselineInfo.justificationStatement && baselineInfo.justificationStatement.length > 0;

            if (!hasProofOfFuel && !hasJustification) {
                errors.push('Either Proof of Fuel Source OR Justification Statement');
            } else if (hasProofOfFuel && hasJustification) {
                setUploadError('You can only upload either Proof of Fuel Source OR Justification Statement, not both. Please remove files from one section.');
                return;
            }
            if (!baselineInfo.additonalInfo || !baselineInfo.additonalInfo.trim()) {
                errors.push('Additional information');
            }
        }
        setLoading(true);
        try {
            // Upload all  files to backend as data URI

            const uploadedProofOfFuelSourceUrls = await Promise.all(
                (baselineInfo.proofOfFuelSource || []).map(file => uploadFileToBackend(file, userId, token))
            );
            const uploadedJustificationStatementUrls = await Promise.all(
                (baselineInfo.justificationStatement || []).map(file => uploadFileToBackend(file, userId, token))
            );
            if (onComplete) onComplete({
                ...facilityInfo,
                ...baselineInfo,
                proofOfFuelSource: uploadedProofOfFuelSourceUrls,
                justificationStatement: uploadedJustificationStatementUrls,
            });
        } catch (err) {
            alert("Failed to upload utility bill(s). Please try again.");
        }
        setLoading(false);
    };
    const facilityStepComplete = validateFacilityForm();
    const baselineStepComplete = validateBaselineForm();
    return (
        <div className="kyc_modal_container">
            <div className="kyc_modal">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 0 }}>
                    {currentStep === 'baseline' && (
                        <button
                            type="button"
                            onClick={handleBackToFacility}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                border: 'none',
                                color: '#2C2C2C',
                                fontWeight: 500,
                                fontSize: 12,
                                cursor: 'pointer',
                                marginRight: 16
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19.0004 11.0006H7.83041L12.7104 6.12065C13.1004 5.73065 13.1004 5.09065 12.7104 4.70065C12.6179 4.60794 12.508 4.5344 12.387 4.48421C12.2661 4.43403 12.1364 4.4082 12.0054 4.4082C11.8744 4.4082 11.7448 4.43403 11.6238 4.48421C11.5028 4.5344 11.3929 4.60794 11.3004 4.70065L4.71041 11.2906C4.61771 11.3832 4.54416 11.493 4.49398 11.614C4.4438 11.735 4.41797 11.8647 4.41797 11.9956C4.41797 12.1266 4.4438 12.2563 4.49398 12.3773C4.54416 12.4982 4.61771 12.6081 4.71041 12.7006L11.3004 19.2906C11.393 19.3832 11.5029 19.4567 11.6239 19.5068C11.7448 19.5569 11.8745 19.5827 12.0054 19.5827C12.1363 19.5827 12.266 19.5569 12.387 19.5068C12.5079 19.4567 12.6178 19.3832 12.7104 19.2906C12.803 19.1981 12.8764 19.0882 12.9265 18.9672C12.9766 18.8462 13.0024 18.7166 13.0024 18.5856C13.0024 18.4547 12.9766 18.3251 12.9265 18.2041C12.8764 18.0831 12.803 17.9732 12.7104 17.8806L7.83041 13.0006H19.0004C19.5504 13.0006 20.0004 12.5506 20.0004 12.0006C20.0004 11.4506 19.5504 11.0006 19.0004 11.0006Z"
                                    fill="black"
                                />
                            </svg>
                            Back
                        </button>
                    )}
                    <div className="title" style={{ textAlign: 'center', flex: 1 }}>Step into the future...</div>
                </div>
                <div className="subtitle" style={{ textAlign: 'center' }}>
                    Complete this KYC step, your safety our priority
                </div>
                <div className="kyc-stepper-row">
                    <div className="">
                        <div className="kyc-step-label ">Facility Information</div>
                        <div className="circle-wrapper ">
                            <div className={facilityStepComplete ? 'circle circle_active' : 'circle'}>
                                <span style={{ color: facilityStepComplete ? '#fff' : undefined }}>1</span>
                            </div>
                        </div>
                    </div>
                    <div className={`kyc-stepper-line ${facilityStepComplete ? 'active' : ''}`} />

                    <div className="kyc-step">
                        <div className="kyc-step-label  ">Baseline Information</div>
                        <div className="circle-wrapper ml-56">
                            <div className={currentStep === 'baseline' && baselineStepComplete ? 'circle circle_active' : 'circle'}>
                                <span style={{ color: currentStep === 'baseline' && baselineStepComplete ? '#fff' : undefined }}>2</span>
                            </div>
                        </div>
                    </div>
                </div>

                {currentStep === 'facility' && (
                    <form className="kyc_grid" onSubmit={handleFacilityComplete}>
                        <div className="kyc_grid">
                            <div>
                                <InputLabel
                                    className="kyc_label"
                                    htmlFor="organizationDescription"
                                >
                                    Facility/Project name
                                    <Tooltip
                                        title={
                                            <Typography variant="h6">
                                                Enter facility/project name                      </Typography>
                                        }
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="-0.5 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                    stroke="#667185"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>{" "}
                                                <path
                                                    d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                    fill="#667185"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </Tooltip>
                                </InputLabel>

                                <OutlinedInput
                                    className="kyc_input"
                                    required
                                    id="organizationDescription"
                                    type="text"
                                    placeholder="Enter facility/project name"
                                    name="organizationDescription"
                                    value={facilityInfo.facilityName}
                                    onChange={(e) => handleFacilityChange('facilityName', e.target.value)}
                                />
                            </div>
                            <div>
                                <InputLabel
                                    className="kyc_label"
                                    htmlFor="organizationDescription"
                                >
                                    Facility/Project description
                                    <Tooltip
                                        title={
                                            <Typography variant="h6">
                                                A brief explanation of what your facility does
                                            </Typography>
                                        }
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="-0.5 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                    stroke="#667185"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>{" "}
                                                <path
                                                    d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                    fill="#667185"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </Tooltip>
                                </InputLabel>

                                <OutlinedInput
                                    className="kyc_input"
                                    required
                                    id="organizationDescription"
                                    type="text"
                                    placeholder="Provide a summary description of the facility to enable an understanding of the nature and its implementation."
                                    name="organizationDescription"
                                    value={facilityInfo.facilityDescription}
                                    onChange={(e) => handleFacilityChange('facilityDescription', e.target.value)}
                                />
                                <div className="kyc_instructions">
                                    <div className=" mt-4 kyc_instructions__title">
                                        Include the following:
                                    </div>
                                    <div className="kyc_instructions__subtitle">
                                        <svg
                                            width="4"
                                            height="4"
                                            viewBox="0 0 4 4"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                                        </svg>
                                        The objectives of the project
                                    </div>
                                    <div className="kyc_instructions__subtitle">
                                        <svg
                                            width="4"
                                            height="4"
                                            viewBox="0 0 4 4"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                                        </svg>
                                        Describe the project activity or activities
                                    </div>
                                    <div className="kyc_instructions__subtitle">
                                        <svg
                                            width="4"
                                            height="4"
                                            viewBox="0 0 4 4"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                                        </svg>
                                        An estimate of annual average, total reductions and removals
                                    </div>
                                </div>
                            </div>
                            <div>
                                <InputLabel className="kyc_label" htmlFor="location">
                                    Facility/ Project location
                                    <Tooltip
                                        title={
                                            <Typography variant="h6">
                                                A precise location of your facility
                                            </Typography>
                                        }
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="-0.5 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                    stroke="#667185"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>{" "}
                                                <path
                                                    d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                    fill="#667185"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </Tooltip>
                                </InputLabel>

                                <OutlinedInput
                                    className="kyc_input"
                                    required
                                    id="location"
                                    type="text"
                                    placeholder="Enter facility/project address"
                                    name="location"
                                    value={facilityInfo.facilityLocation}
                                    onChange={(e) => handleFacilityChange('facilityLocation', e.target.value)}
                                />
                            </div>
                            <div className="kyc_group">
                                <div>
                                    <InputLabel className="kyc_label" htmlFor="location">
                                        Facility/project start date

                                        <Tooltip
                                            title={
                                                <Typography variant="h6">
                                                    Enter  facility/project start
                                                </Typography>
                                            }
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="-0.5 0 25 25"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                                <g
                                                    id="SVGRepo_tracerCarrier"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></g>
                                                <g id="SVGRepo_iconCarrier">
                                                    {" "}
                                                    <path
                                                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                        stroke="#667185"
                                                        stroke-width="1.5"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    ></path>{" "}
                                                    <path
                                                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                        fill="#667185"
                                                    ></path>{" "}
                                                </g>
                                            </svg>
                                        </Tooltip>
                                    </InputLabel>

                                    <input
                                        className="kyc_input kyc_input-date"
                                        required
                                        id="location"
                                        type="date"
                                        placeholder="Enter facility/project start date"
                                        name="location"
                                        value={facilityInfo.projectStartDate}
                                        onChange={(e) => handleFacilityChange('projectStartDate', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <InputLabel className="kyc_label" htmlFor="location">
                                        Facility end date

                                        <Tooltip
                                            title={
                                                <Typography variant="h6">
                                                    Enter facility/project end date
                                                </Typography>
                                            }
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="-0.5 0 25 25"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                                <g
                                                    id="SVGRepo_tracerCarrier"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></g>
                                                <g id="SVGRepo_iconCarrier">
                                                    {" "}
                                                    <path
                                                        d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                        stroke="#667185"
                                                        stroke-width="1.5"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    ></path>{" "}
                                                    <path
                                                        d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                        fill="#667185"
                                                    ></path>{" "}
                                                </g>
                                            </svg>
                                        </Tooltip>
                                    </InputLabel>

                                    <input
                                        className="kyc_input kyc_input-date"
                                        // required
                                        id="location"
                                        type="date"
                                        placeholder="Enter facility/project end date"
                                        name="location"
                                        value={facilityInfo.projectEndDate}
                                        onChange={(e) => handleFacilityChange('projectEndDate', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: 0 }}>

                                <InputLabel
                                    className="kyc_label"
                                    htmlFor="quantifiedBasedYearEmission"
                                >
                                    Facility/Project fuel source
                                    <Tooltip
                                        title={
                                            <Typography variant="h6">
                                                What the fuel source for this project is
                                            </Typography>
                                        }
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="-0.5 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                    stroke="#667185"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>{" "}
                                                <path
                                                    d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                    fill="#667185"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </Tooltip>
                                </InputLabel>
                                <div className="kyc_dropdown_checkbox_container">
                                    <div className="kyc_dropdown" style={{ position: 'relative' }}>
                                        <div
                                            className="kyc_input kyc_dropdown_input"
                                            style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                            onClick={() => setShowDropdownFuelSource(prev => !prev)}
                                        >
                                            <span>
                                                {facilityInfo.fuelSource
                                                    ? facilityInfo.fuelSource === 'others' && facilityInfo.fuelSourceOther && facilityInfo.fuelSourceOther.trim() !== ''
                                                        ? facilityInfo.fuelSourceOther
                                                        : facilityInfo.fuelSource
                                                    : 'Select facility/project fuel source'}
                                            </span>
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{ marginRight: 24, transition: 'transform 0.2s', transform: showDropdownFuelSource ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                            >
                                                <path d="M6 8L10 12L14 8" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        {showDropdownFuelSource && (
                                            <div className="kyc_dropdown_menu" style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: 8, width: '100%', marginTop: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                {[
                                                    { label: 'Coal', value: 'Coal' },
                                                    { label: 'Diesel', value: 'Diesel' },
                                                    { label: 'Natural gas', value: 'Natural gas' },
                                                    { label: 'Petroleum', value: 'Petroleum' },
                                                    { label: 'LPG', value: 'LPG' },
                                                    { label: 'Propane', value: 'propane' },
                                                    { label: 'Kerosene', value: 'kerosene' },
                                                    { label: 'Shale oil', value: 'shale' },
                                                    { label: 'Others', value: 'others' },
                                                ].map(option => (
                                                    <div key={option.value} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px' }}>
                                                        {facilityInfo.fuelSource === option.value ? (
                                                            <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => {
                                                                // Deselect current option
                                                                handleFacilityChange('fuelSource', '');
                                                                if (option.value === 'others') handleFacilityChange('fuelSourceOther', '');
                                                            }} style={{ cursor: 'pointer' }}>
                                                                <rect x="1" y="1" width="16" height="16" rx="4" stroke="url(#paint0_linear_2889_18037)" strokeWidth="1.33333" />
                                                                <path d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325" stroke="url(#paint1_linear_2889_18037)" strokeLinecap="round" strokeLinejoin="round" />
                                                                <defs>
                                                                    <linearGradient id="paint0_linear_2889_18037" x1="1" y1="1.36719" x2="20.0689" y2="10.7886" gradientUnits="userSpaceOnUse"><stop stopColor="#006633" /><stop offset="1" stopColor="#02A4CC" /></linearGradient>
                                                                    <linearGradient id="paint1_linear_2889_18037" x1="5.5" y1="6.948" x2="12.5203" y2="11.8039" gradientUnits="userSpaceOnUse"><stop stopColor="#006633" /><stop offset="1" stopColor="#02A4CC" /></linearGradient>
                                                                </defs>
                                                            </svg>
                                                        ) : (
                                                            <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => {
                                                                // Select this option and clear previous selection
                                                                handleFacilityChange('fuelSource', option.value);
                                                                // Clear the "others" text field if switching away from "others"
                                                                if (option.value !== 'others') handleFacilityChange('fuelSourceOther', '');
                                                            }} style={{ cursor: 'pointer' }}>
                                                                <rect x="1" y="1" width="16" height="16" rx="4" stroke="url(#paint0_linear_2889_18043)" strokeWidth="1.33333" />
                                                                <defs>
                                                                    <linearGradient id="paint0_linear_2889_18043" x1="1" y1="1.36719" x2="20.0689" y2="10.7886" gradientUnits="userSpaceOnUse"><stop stopColor="#006633" /><stop offset="1" stopColor="#02A4CC" /></linearGradient>
                                                                </defs>
                                                            </svg>
                                                        )}
                                                        <span style={{ marginLeft: 8, fontSize: 12 }}>{option.label}</span>
                                                        {option.value === 'others' && facilityInfo.fuelSource === 'others' && (
                                                            <OutlinedInput
                                                                className="kyc_input"
                                                                style={{ marginLeft: 12, width: 180, height: 32 }}
                                                                placeholder="Please specify"
                                                                value={facilityInfo.fuelSourceOther || ''}
                                                                onChange={e => handleFacilityChange('fuelSourceOther', e.target.value)}

                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                                <div style={{ textAlign: 'right', padding: '8px 12px' }}>
                                                    <Button size="small" variant="outlined" onClick={() => setShowDropdownFuelSource(false)}>Done</Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <InputLabel
                                    className="kyc_label"
                                    htmlFor="organizationDescription"
                                >
                                    Additional information relevant to the project
                                    <Tooltip
                                        title={
                                            <Typography variant="h6">
                                                Provide any additional information relevant to bearing eligibility of the project.
                                            </Typography>
                                        }
                                    >
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="-0.5 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                            <g
                                                id="SVGRepo_tracerCarrier"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></g>
                                            <g id="SVGRepo_iconCarrier">
                                                {" "}
                                                <path
                                                    d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                    stroke="#667185"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                ></path>{" "}
                                                <path
                                                    d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                    fill="#667185"
                                                ></path>{" "}
                                            </g>
                                        </svg>
                                    </Tooltip>
                                </InputLabel>

                                <OutlinedInput
                                    className="kyc_input"
                                    required
                                    id="organizationDescription"
                                    type="text"
                                    placeholder="Provide a summary description of the facility to enable an understanding of the nature and its implementation."
                                    name="organizationDescription"
                                    value={facilityInfo.additonalInfo}
                                    onChange={(e) => handleFacilityChange('additonalInfo', e.target.value)}
                                />
                                <div className="kyc_instructions">
                                    <div className=" mt-4 kyc_instructions__title">
                                        Include any additional information that may have a bearing on the eligibility of the project.
                                    </div>

                                </div>
                            </div>


                            <div>
                                <InputLabel
                                    className="kyc_label"
                                    htmlFor="quantifiedBasedYearEmission"
                                >
                                    Double counting and participation under other GHG programs

                                </InputLabel>
                                <div className="kyc-checkbox-grid">
                                    <div className="kyc-checkbox-grid__title">
                                        Is the project receiving or seeking credit for reductions
                                        and removals from a project activity under another GHG
                                        program?
                                    </div>
                                    <div className="kyc-checkbox-grid__check-box">
                                        <div>
                                            {doubleCounting !== "true" && (
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 18 18"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => setDoubleCounting("true")}
                                                >
                                                    <rect
                                                        x="1"
                                                        y="1"
                                                        width="16"
                                                        height="16"
                                                        rx="4"
                                                        stroke="url(#paint0_linear_2889_18043)"
                                                        stroke-width="1.33333"
                                                    />
                                                    <defs>
                                                        <linearGradient
                                                            id="paint0_linear_2889_18043"
                                                            x1="1"
                                                            y1="1.36719"
                                                            x2="20.0689"
                                                            y2="10.7886"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            )}
                                            {doubleCounting === "true" && (
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 18 18"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => setDoubleCounting("")}
                                                >
                                                    <rect
                                                        x="1"
                                                        y="1"
                                                        width="16"
                                                        height="16"
                                                        rx="4"
                                                        stroke="url(#paint0_linear_2889_18037)"
                                                        stroke-width="1.33333"
                                                    />
                                                    <path
                                                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                                                        stroke="url(#paint1_linear_2889_18037)"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                    <defs>
                                                        <linearGradient
                                                            id="paint0_linear_2889_18037"
                                                            x1="1"
                                                            y1="1.36719"
                                                            x2="20.0689"
                                                            y2="10.7886"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                        <linearGradient
                                                            id="paint1_linear_2889_18037"
                                                            x1="5.5"
                                                            y1="6.948"
                                                            x2="12.5203"
                                                            y2="11.8039"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            )}
                                            Yes
                                        </div>
                                        <div>
                                            {doubleCounting !== "false" && (
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 18 18"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => setDoubleCounting("false")}
                                                >
                                                    <rect
                                                        x="1"
                                                        y="1"
                                                        width="16"
                                                        height="16"
                                                        rx="4"
                                                        stroke="url(#paint0_linear_2889_18043)"
                                                        stroke-width="1.33333"
                                                    />
                                                    <defs>
                                                        <linearGradient
                                                            id="paint0_linear_2889_18043"
                                                            x1="1"
                                                            y1="1.36719"
                                                            x2="20.0689"
                                                            y2="10.7886"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            )}
                                            {doubleCounting === "false" && (
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 18 18"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    onClick={() => setDoubleCounting("")}
                                                >
                                                    <rect
                                                        x="1"
                                                        y="1"
                                                        width="16"
                                                        height="16"
                                                        rx="4"
                                                        stroke="url(#paint0_linear_2889_18037)"
                                                        stroke-width="1.33333"
                                                    />
                                                    <path
                                                        d="M5.5 9.49992L7.83333 11.8333L12.5 6.83325"
                                                        stroke="url(#paint1_linear_2889_18037)"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                    <defs>
                                                        <linearGradient
                                                            id="paint0_linear_2889_18037"
                                                            x1="1"
                                                            y1="1.36719"
                                                            x2="20.0689"
                                                            y2="10.7886"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                        <linearGradient
                                                            id="paint1_linear_2889_18037"
                                                            x1="5.5"
                                                            y1="6.948"
                                                            x2="12.5203"
                                                            y2="11.8039"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stop-color="#006633" />
                                                            <stop offset="1" stop-color="#02A4CC" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            )}
                                            No
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button className="kyc_submit" type="submit">Continue</button>
                        </div>
                    </form>
                )}


                {currentStep === 'baseline' && (
                    <form className="kyc_grid" onSubmit={handleDone}>
                        <div>
                            <InputLabel className="kyc_label" htmlFor="baseline">
                                Baseline scenario
                                <Tooltip title={<Typography variant="h6">
                                    Provide a summary description of the facility activities before this new project initiation.
                                </Typography>}>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="-0.5 0 25 25"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g
                                            id="SVGRepo_tracerCarrier"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                            {" "}
                                            <path
                                                d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                stroke="#667185"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></path>{" "}
                                            <path
                                                d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                fill="#667185"
                                            ></path>{" "}
                                        </g>
                                    </svg>                </Tooltip>
                            </InputLabel>
                            <OutlinedInput className="kyc_input" required id="baseline" type="text"
                                placeholder="Provide a summary description of the facility activities before this new project initiation.
"
                                name="baseline" value={baselineInfo.baseline} onChange={e => handleBaselineChange('baseline', e.target.value)} />
                            <div className="kyc_instructions">
                                <div className=" mt-4 kyc_instructions__title">Include the following:</div>
                                <div className="kyc_instructions__subtitle">
                                    <svg
                                        width="4"
                                        height="4"
                                        viewBox="0 0 4 4"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                                    </svg>
                                    Conditions existing prior to project initiation</div>
                                <div className="kyc_instructions__subtitle">
                                    <svg
                                        width="4"
                                        height="4"
                                        viewBox="0 0 4 4"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
                                    </svg>
                                    Demonstrate that the project is aimed at reducing greenhouse (GHG) emissions</div>
                            </div>
                        </div>
                        <div>
                            <InputLabel
                                className="kyc_label"
                                htmlFor="quantifiedBasedYearEmission"
                            >
                                Fuel source in baseline scenario

                                <Tooltip
                                    title={
                                        <Typography variant="h6">
                                            What fuel source was used before this project started
                                        </Typography>
                                    }
                                >
                                    <svg width="18" height="18" viewBox="-0.5 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="#667185" strokeWidth="1.5" /><text x="12" y="16" textAnchor="middle" fontSize="12" fill="#667185">i</text></svg>

                                </Tooltip>
                            </InputLabel>
                            <div style={{ position: 'relative', width: '100%' }}>

                                <select
                                    className="kyc_input" required id="fuelSourceBaseline" type="text" placeholder="Enter fuel source in baseline scenario" name="fuelSourceBaseline" value={baselineInfo.fuelSourceBaseline} onChange={e => handleBaselineChange('fuelSourceBaseline', e.target.value)}
                                    style={{
                                        appearance: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value="">
                                        Select a fuel source in baseline scenario
                                    </option>
                                    <option value="Coal">Coal</option>
                                    <option value="Diesel">Diesel</option>
                                    <option value="Natural gas">Natural gas</option>
                                    <option value="Petroleum">Petroleum</option>
                                    <option value="Kerosene">Kerosene</option>
                                    <option value="Others">Others</option>
                                </select>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{
                                        position: 'absolute',
                                        right: '24px',        // spacing from right edge
                                        top: '50%',           // center vertically
                                        transform: 'translateY(-50%) rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <path
                                        d="M6 8L10 12L14 8"
                                        stroke="#667085"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>



                        {/* Proof of baseline fuel source - WITH PREVIEW */}
                        <div >
                            <InputLabel className="kyc_label" htmlFor="quantifiedBasedYearEmission">
                                Proof of energy source in baseline scenario
                                <Tooltip>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="-0.5 0 25 25"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g
                                            id="SVGRepo_tracerCarrier"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                            {" "}
                                            <path
                                                d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                stroke="#667185"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></path>{" "}
                                            <path
                                                d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                fill="#667185"
                                            ></path>{" "}
                                        </g>
                                    </svg>
                                </Tooltip>
                            </InputLabel>

                            <div style={{
                                border: '1px dashed #667185',
                                borderRadius: '12px',
                                padding: '24px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                                position: 'relative'
                            }}>
                                <input
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    id="proofOf-fuel-source-upload"
                                    type="file"
                                    multiple
                                    onChange={handleProofOfFuelSourceUpload}
                                />
                                <InputLabel htmlFor="proofOf-fuel-source-upload" style={{ fontSize: '14px', fontWeight: 400, color: '#101828', marginBottom: '8px', display: 'block' }}>
                                    Upload invoice, photo, or fuel receipt
                                </InputLabel>

                                <div style={{ fontSize: '12px', color: '#667185', fontWeight: 400, lineHeight: '18px', marginBottom: '8px' }}>
                                    <div>Image can be PDF/JPEG/PNG allowed, max 5MB</div>
                                </div>

                                <label htmlFor="proofOf-fuel-source-upload">
                                    <Button variant="outlined" component="span" style={{ border: '1px solid #D0D5DD', borderRadius: '8px', padding: '10px 16px', color: '#344054', fontSize: '14px', fontWeight: 500, textTransform: 'none', backgroundColor: '#FFFFFF', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', marginTop: '8px' }}>
                                        Browse File
                                    </Button>
                                </label>
                            </div>

                            {/* FILE PREVIEW GALLERY */}
                            <FilePreviewGallery files={baselineInfo.proofOfFuelSource} onRemove={handleRemoveProofOfFuelSource} />

                            <div className="flex flex-col gap-2 w-[286px]">
                                <p className="text-[10px] mt-6 font-medium text-[#282828] leading-[12px]">
                                    Acceptable documents include:
                                </p>

                                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 list-disc pl-5">
                                    <li className="text-[10px] text-[#667185] leading-[12px]">Fuel purchase bills</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">Equipment invoices/bills</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">Operation logs</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">Equipment photos</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">Permits/License</li>
                                </ul>
                            </div>

                        </div>
                        <span className=" mt-[-18px] flex items-center justify-center text-[16px] font-bold w-full">
                            OR
                        </span>
                        {/* Baseline Justification Statement - WITH PREVIEW */}
                        <div style={{ marginBottom: '16px' }}>
                            <InputLabel className="kyc_label" htmlFor="quantifiedBasedYearEmission">
                                Baseline scenario justification statement

                                <Tooltip>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="-0.5 0 25 25"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                        <g
                                            id="SVGRepo_tracerCarrier"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        ></g>
                                        <g id="SVGRepo_iconCarrier">
                                            {" "}
                                            <path
                                                d="M12 21.5C17.1086 21.5 21.25 17.3586 21.25 12.25C21.25 7.14137 17.1086 3 12 3C6.89137 3 2.75 7.14137 2.75 12.25C2.75 17.3586 6.89137 21.5 12 21.5Z"
                                                stroke="#667185"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            ></path>{" "}
                                            <path
                                                d="M12.9309 8.15005C12.9256 8.39231 12.825 8.62272 12.6509 8.79123C12.4767 8.95974 12.2431 9.05271 12.0008 9.05002C11.8242 9.04413 11.6533 8.98641 11.5093 8.884C11.3652 8.7816 11.2546 8.63903 11.1911 8.47415C11.1275 8.30927 11.1139 8.12932 11.152 7.95675C11.19 7.78419 11.278 7.6267 11.405 7.50381C11.532 7.38093 11.6923 7.29814 11.866 7.26578C12.0397 7.23341 12.2192 7.25289 12.3819 7.32181C12.5446 7.39072 12.6834 7.506 12.781 7.65329C12.8787 7.80057 12.9308 7.97335 12.9309 8.15005ZM11.2909 16.5301V11.1501C11.2882 11.0556 11.3046 10.9615 11.3392 10.8736C11.3738 10.7857 11.4258 10.7057 11.4922 10.6385C11.5585 10.5712 11.6378 10.518 11.7252 10.4822C11.8126 10.4464 11.9064 10.4286 12.0008 10.43C12.094 10.4299 12.1863 10.4487 12.272 10.4853C12.3577 10.5218 12.4352 10.5753 12.4997 10.6426C12.5642 10.7099 12.6143 10.7895 12.6472 10.8767C12.6801 10.9639 12.6949 11.0569 12.6908 11.1501V16.5301C12.6908 16.622 12.6727 16.713 12.6376 16.7979C12.6024 16.8828 12.5508 16.96 12.4858 17.025C12.4208 17.09 12.3437 17.1415 12.2588 17.1767C12.1738 17.2119 12.0828 17.23 11.9909 17.23C11.899 17.23 11.8079 17.2119 11.723 17.1767C11.6381 17.1415 11.5609 17.09 11.4959 17.025C11.4309 16.96 11.3793 16.8828 11.3442 16.7979C11.309 16.713 11.2909 16.622 11.2909 16.5301Z"
                                                fill="#667185"
                                            ></path>{" "}
                                        </g>
                                    </svg>
                                </Tooltip>
                            </InputLabel>

                            <div style={{
                                border: '1px dashed #667185',
                                borderRadius: '12px',
                                padding: '24px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                                position: 'relative'
                            }}>
                                <input
                                    accept="image/*,.pdf"
                                    style={{ display: 'none' }}
                                    id="justification-statement-upload"
                                    type="file"
                                    multiple
                                    onChange={handleJustificationStatementUpload}
                                />
                                <InputLabel htmlFor="justification-statement-upload" style={{ fontSize: '14px', fontWeight: 400, color: '#101828', marginBottom: '8px', display: 'block' }}>
                                    Upload document
                                </InputLabel>

                                <div style={{ fontSize: '12px', color: '#667185', fontWeight: 400, lineHeight: '18px', marginBottom: '8px' }}>
                                    <div>PDF/DOCX file up to 5MB</div>
                                </div>

                                <label htmlFor="justification-statement-upload">
                                    <Button variant="outlined" component="span" style={{ border: '1px solid #D0D5DD', borderRadius: '8px', padding: '10px 16px', color: '#344054', fontSize: '14px', fontWeight: 500, textTransform: 'none', backgroundColor: '#FFFFFF', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', marginTop: '8px' }}>
                                        Browse File
                                    </Button>
                                </label>
                            </div>

                            {/* FILE PREVIEW GALLERY */}
                            <FilePreviewGallery files={baselineInfo.justificationStatement} onRemove={handleRemoveJustificationStatement} />

                            <div className="flex flex-col gap-2 w-[578px]">
                                <p className="text-[10px] mt-6 font-medium text-[#282828] leading-[12px]">This is for facilities with no prior system installed — please upload a formal statement on company letterhead describing:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li className="text-[10px] text-[#667185] leading-[12px]">The baseline fuel/equipment that would have been used.</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">The chosen cleaner option and its capacity.</li>
                                    <li className="text-[10px] text-[#667185] leading-[12px]">The reason for deviation (e.g., environmental considerations).</li>
                                </ul>
                            </div>
                        </div>

                        {/* Show upload error if any */}
                        {uploadError && (
                            <div style={{
                                color: '#d32f2f',
                                fontSize: '14px',
                                padding: '8px 12px',
                                backgroundColor: '#ffebee',
                                borderRadius: '4px',
                                marginTop: '2px'
                            }}>
                                {uploadError}
                            </div>
                        )}


                        <div className="kyc_group">
                            <button className="kyc_submit" type="submit">Done</button>
                        </div>
                    </form>
                )}

            </div>
        </div>
    );
};


export default KYCGovernanceNonGreenForm; 